/* eslint-disable import/no-named-default */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { Col, Row } from 'react-styled-flexboxgrid';
import { Link, withPrefix } from 'gatsby';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import _ from 'lodash';
import { object } from 'prop-types';
import styled, { css } from 'styled-components';
import { SHOP_URL } from '../../../constants';
import { useRootState } from '../../../context/root-state.context';
import { useShopifyStore } from '../../../context/store-context';
import { useViewport } from '../../../context/viewport.context';
import { useIsomorphicLayoutEffect } from '../../../hooks';
import ArrowLNavy from '../../../icons/arrow-LNavy.svg';
import {
  btnLgPrimaryMediumDisabledOrangeStyles,
  btnLgPrimaryMediumOrangeStyles,
  btnSmPrimaryMediumDisabledOrangeStyles,
  btnSmPrimaryMediumOrangeStyles,
} from '../../../styles/buttons';
import { spacerSStyles } from '../../../styles/structureSpacing';
import {
  bodyMBoldBrownStyles,
  bodyMRegularUtopiaStyles,
  bodySRegularBrownStyles,
  bodySRegularUtopiaStyles,
  bodyXsBoldBrownStyles,
  bodyXsRegularBrownStyles,
  bodyXsRegularUtopiaStyles,
  bodyXxlBoldBrownStyles,
  bodyXxsBoldBrownStyles,
  bodyXxsItalicUtopiaStyles,
  bodyXxsRegularBrownStyles,
  bodyXxsRegularUtopiaStyles,
  bodyXxsSemiBoldUtopiaStyles,
  eyebrowMStyles,
  eyebrowSStyles,
  eyebrowXsStyles,
} from '../../../styles/typography';
import { bpWidth, colors } from '../../../styles/variables';
import { currencyFormatter } from '../../../utils/currencyFormatter';
import { renderRichTextReact } from '../../../utils/storyblokRichText';
import BottomCarousel from './BottomCarousel';
import CollapseArrow from './CollapseArrow';
import LimitedEditionTag from './LimitedEditionTag';
import LooxRating from '../../Product/LooxRating';
import LooxReviews from './LooxReviews';
import ProductVariantCarousels from './ProductVariantCarousels';
import ProductVariantDescription from './ProductVariantDescription';
import ProductVariantSelector from './ProductVariantSelector';
import { default as RowTW } from '../../FlexboxGrid/Row.jsx';
import { default as ColTW } from '../../FlexboxGrid/Col.jsx';
import FullWidthImageWithOneColumnCaption from '../FullWidthImageWithOneColumnCaption';
import FullWidthImageWithTwoColumnCaption from '../FullWidthImageWithTwoColumnCaption';
import ProductCard from './ProductCard';
import HorizontalScroller from '../Carousels/HorizontalScroller';
import ShopPayInstallmentsLogoWhite from '../../../img/shop-pay-affirm-logo-white.png';
import ShopPayInstallmentsLogoBlack from '../../../img/shop-pay-affirm-logo-black.png';

const ShopProductCol = styled(Col)`
  margin-top: ${({ sectionMarginTop = 100 }) => sectionMarginTop}px;
`;

const BackRow = styled(Row)`
  margin-bottom: 20px;

  @media (${bpWidth.mobile}) {
    > div {
      margin-left: 30px !important;
    }
  }
`;

const BackLink = styled(Link)`
  ${bodyXxsRegularBrownStyles};
  cursor: pointer;
  text-decoration: none;
  ${({ starWarsTheme }) => starWarsTheme && 'color: #fff'};

  svg {
    margin-right: 10px;
    transform: rotate(180deg);

    path {
      ${({ starWarsTheme }) => starWarsTheme && 'stroke: #fff'};
    }
  }
`;

const ProductPricesCtaContainer = styled.div`
  display: flex;
  align-items: baseline;
  flex-direction: column;
  padding-bottom: 50px;

  img {
    height: 50px;
  }

  p {
    margin-bottom: 0;
  }

  @media (${bpWidth.desktopSm}) {
    margin-right: 55px;
    padding-bottom: 40px;

    img {
      height: 30px;
    }
  }

  @media (${bpWidth.tablet}) {
    margin-right: 0;
    margin-top: 65px;
  }

  @media (${bpWidth.mobile}) {
    margin-top: 75px;
  }

  .showfields-wrapper {
    margin-top: 40px;

    a {
      ${bodyMRegularUtopiaStyles};
      color: rgb(0, 180, 220);
    }

    a:visited {
      color: rgb(0, 180, 220);
      text-decoration: none;
    }

    @media (${bpWidth.desktopSm}) {
      a {
        ${bodySRegularUtopiaStyles};
        color: rgb(0, 180, 220);
      }
    }

    @media (${bpWidth.tablet}) {
      margin-top: 20px;
    }
  }
`;

const ProductPriceTotal = styled.p`
  ${bodyXxlBoldBrownStyles};
  padding-bottom: 10px;
  ${({ starWarsTheme }) => starWarsTheme && 'color: #fff'};

  @media (${bpWidth.desktopSm}) {
    ${bodyMBoldBrownStyles};
    ${({ starWarsTheme }) => starWarsTheme && 'color: #fff'};
  }
`;

const ColorsCtaContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (${bpWidth.tablet}) {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

  @media (${bpWidth.mobile}) {
    flex-direction: column;
  }
`;

const CurrentColorCopy = styled.div`
  ${eyebrowSStyles};
  color: ${colors.gray200};
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 20px;
  min-height: 40px;

  @media (${bpWidth.desktopSm}) {
    ${eyebrowXsStyles};
    color: ${colors.gray200};
  }
`;

const CartCtaContainer = styled.div`
  div {
    ${bodySRegularBrownStyles};
    color: ${colors.gray400};
    padding-top: 10px;
  }

  @media (${bpWidth.tablet}) {
    display: flex;
    flex-direction: column;
    align-items: end;
    text-align: right;
  }

  @media (${bpWidth.mobile}) {
    align-items: start;
    text-align: left;
  }
`;

const CartCta = styled.button`
  ${btnLgPrimaryMediumOrangeStyles};
  cursor: pointer;

  @media (${bpWidth.desktopSm}) {
    ${btnSmPrimaryMediumOrangeStyles};
  }

  &.disabled {
    ${btnLgPrimaryMediumDisabledOrangeStyles};

    @media (${bpWidth.desktopSm}) {
      ${btnSmPrimaryMediumDisabledOrangeStyles};
    }
  }

  // button glow hover styling
  ${({ starWarsStyle }) => {
    if (starWarsStyle) {
      return css`
        transition: box-shadow 0.1s ease-in-out;

        &:hover {
          box-shadow: 0 0 20px 5px ${colors.orange400};
          background-color: ${colors.orange400};
        }
      `;
    }

    return undefined;
  }};
`;

const ProductScrollableInfo = styled.div`
  overflow-y: auto;
  max-height: 490px;
  padding-right: 10px;
  ${({ starWarsTheme }) => starWarsTheme && `color: ${colors.beige100}`};

  h2 {
    ${eyebrowMStyles}
    ${({ starWarsTheme }) => starWarsTheme && `color: ${colors.beige100}`};
  }

  p,
  span,
  p span {
    ${bodyMRegularUtopiaStyles};
    ${({ starWarsTheme }) => starWarsTheme && `color: ${colors.beige100}`};
  }

  svg,
  svg path {
    ${({ starWarsTheme }) => starWarsTheme && 'stroke: #fff'};
  }

  button,
  h3,
  h3 *,
  .description,
  .description i,
  .description p,
  .description b,
  .description a {
    ${({ starWarsTheme }) => starWarsTheme && `color: ${colors.beige100}!important`};
  }

  @media (${bpWidth.desktopSm}) {
    max-height: 300px;
    margin-right: 55px;
    ${({ starWarsTheme }) => starWarsTheme && `color: ${colors.beige100}`};

    h2 {
      ${eyebrowSStyles};
      ${({ starWarsTheme }) => starWarsTheme && `color: ${colors.beige100}`};
    }

    p,
    span,
    p span {
      ${bodySRegularUtopiaStyles};
      ${({ starWarsTheme }) => starWarsTheme && `color: ${colors.beige100}`};
    }
  }
  @media (${bpWidth.tablet}) {
    max-height: unset;
    margin-right: 0;
    padding-right: 0;
    ${({ starWarsTheme }) => starWarsTheme && `color: ${colors.beige100}`};
  }
`;

const ScrollableSections = styled.div`
  ${spacerSStyles};
  ${({ starWarsTheme }) =>
    !starWarsTheme
      ? `border-top: 1px solid ${colors.gray100};`
      : `position: relative;
    &:after {
      max-width: 100%;
      overflow: hidden;
      content: url('/shop-sw-divider.svg');
      position: absolute;
      bottom: 0;
    }`};

  button {
    ${eyebrowMStyles};
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    padding: 0 2px 0 0;
    width: 100%;
    margin: 0;

    .arrow {
      filter: none;
    }

    &:hover {
      ${eyebrowMStyles};

      .arrow {
        filter: none;
      }
    }
  }

  &:last-child {
    ${({ starWarsTheme }) => !starWarsTheme && `border-bottom: 1px solid ${colors.gray100};`};
  }

  div.description {
    padding-top: 20px;
  }

  @media (${bpWidth.desktopSm}) {
    button {
      ${eyebrowSStyles};
      padding: 0;

      &:hover {
        ${eyebrowSStyles};
      }
    }
  }
`;

const PropositionSection = styled.div`
  padding-top: 30px;

  h3 {
    ${bodyXsRegularBrownStyles};

    span {
      ${bodyXsRegularBrownStyles};
      color: ${colors.cerulean400} !important;
      cursor: pointer;
    }
  }
  div {
    display: flex;
    flex-direction: column;

    h6 {
      ${bodyXsBoldBrownStyles};
      color: ${colors.orange700};
      display: flex;
      flex-direction: row;
    }

    span,
    p {
      ${bodyXsRegularUtopiaStyles};
      white-space: pre-wrap;

      a {
        text-decoration: none;
        color: ${colors.navy600};
      }
    }
  }

  @media (${bpWidth.desktopSm}) {
    padding-top: 20px;

    h3 {
      ${bodyXxsRegularBrownStyles};

      span {
        ${bodyXxsRegularBrownStyles};
        color: ${colors.cerulean400};
      }
    }

    div {
      h6 {
        ${bodyXxsBoldBrownStyles};
        color: ${colors.orange700};
      }

      span,
      p {
        ${bodyXxsRegularUtopiaStyles};
        i {
          ${bodyXxsItalicUtopiaStyles};
        }

        b {
          ${bodyXxsSemiBoldUtopiaStyles};
          font-weight: bold;
        }
      }
    }
  }

  b,
  strong {
    span {
      font-weight: bold;
    }
  }
`;

const BottomContainerRow = styled(Row)`
  display: flex;
  flex-shrink: 0;
`;

const ProductReviewsRow = styled(Row)`
  padding-bottom: 50px;
`;

const ShopProduct = ({ blok }) => {
  const { viewWidth } = useViewport();
  const { shopifyProduct, productColors } = useRootState();
  const { addVariantToCart, fetchProductOrVariant } = useShopifyStore();
  const expandText = 'Expand Proposition';
  const collapseText = 'Collapse Proposition';

  const [sectionMarginTop, setSectionMarginTop] = useState(100);
  const [productVariant, setProductVariant] = useState({});
  const [isExpandShipping, setIsExpandShipping] = useState(false);
  const [isExpandWhatsIncluded, setIsExpandWhatsIncluded] = useState(false);
  const [isExpandProductDetails, setIsExpandProductDetails] = useState(false);
  const [isExpandProposition, setIsExpandProposition] = useState(false);
  const [propositionTitle, setPropositionTitle] = useState(expandText);

  const productPageTitleModified = useMemo(() => {
    const title = _.get(blok, 'productPageTitle', '');

    if (!title.includes('Grogu')) {
      return blok.productPageTitle; // Return the title as is if it doesn't include "Grogu"
    }

    // If the title includes "Grogu", wrap "mini" in <i></i>
    const parts = title.split(/(mini)/i); // Split the title into parts, keeping "mini" as a separate element
    // eslint-disable-next-line react/no-array-index-key
    return <>{parts.map((part, index) => (part.toLowerCase() === 'mini' ? <i key={index}>{part}</i> : part))}</>;
  }, [blok]);

  // shopifyProduct/blok/productColors are all synchronously available (no live network call),
  // so this merge can run during render instead of waiting for an effect.
  const productVariants = useMemo(() => {
    if (!shopifyProduct) return [];

    const { variants: shopifyVariants = [] } = shopifyProduct;
    const { variants: blokVariants = [] } = blok;
    const filteredVariants = blokVariants.filter((v) => v.enable);
    return _.intersectionWith(shopifyVariants, filteredVariants, (shopifyVar, blokVar) => {
      const productColor = productColors.find((pc) => pc.value === blokVar.color);
      return shopifyVar.title === productColor?.name && _.assign(shopifyVar, blokVar);
    }).map((variant) => ({ ...variant, productTitle: shopifyProduct.title }));
  }, [shopifyProduct, blok, productColors]);

  const [currentVariant, setCurrentVariant] = useState(() => productVariants[0] ?? null);
  const hasCheckedUrlColorParam = useRef(false);

  useEffect(() => {
    if (_.isEmpty(productVariants)) return;

    // Honor a ?color= URL param once — needs `window`, so it can't be part of the synchronous
    // default above and has to run client-side after mount.
    if (!hasCheckedUrlColorParam.current) {
      hasCheckedUrlColorParam.current = true;
      const urlParams = new URLSearchParams(window.location.search);
      const colorParam = urlParams.get('color');
      const colorVariant = colorParam && productVariants.find((variant) => _.camelCase(variant.title) === colorParam);
      if (colorVariant) {
        setCurrentVariant(colorVariant);
        return;
      }
    }

    // re-sync the currently selected variant with the freshly merged data (e.g. editor updates)
    setCurrentVariant((prevVariant) => {
      if (!prevVariant) return productVariants[0] ?? null;
      return productVariants.find((v) => v.title === prevVariant.title) || prevVariant;
    });
  }, [productVariants]);

  const isAvailable = useMemo(() => {
    if (!_.isEmpty(productVariant)) {
      return productVariant.availableForSale && productVariant.quantityAvailable > 0;
    }
    return false;
  }, [productVariant]);

  const handleAddToCart = useCallback(
    (_variant) => {
      if (isAvailable && _variant) {
        addVariantToCart(_variant, 1);
      }
    },
    [isAvailable, addVariantToCart],
  );

  useIsomorphicLayoutEffect(() => {
    const headerContainerRow = document.getElementById('headerContainerRow');
    if (!headerContainerRow) {
      return undefined;
    }

    const applySectionMarginTop = () => setSectionMarginTop(headerContainerRow.getBoundingClientRect().height + 40);
    applySectionMarginTop();

    const resizeObserver = new ResizeObserver(applySectionMarginTop);
    resizeObserver.observe(headerContainerRow);

    return () => resizeObserver.disconnect();
  }, [viewWidth]);

  // Price based on color variant selected
  const selectedVariantPrice = useMemo(() => {
    if (currentVariant) {
      const productPriceTotal = currencyFormatter(currentVariant.price, { minimumFractionDigits: 0 });

      return <ProductPriceTotal starWarsTheme={blok.starWarsTheme}>{productPriceTotal}</ProductPriceTotal>;
    }

    return undefined;
  }, [currentVariant, blok]);

  const handleVariantChanged = (item) => {
    setCurrentVariant(item);
  };

  // set product variant from Shopify API client
  useEffect(() => {
    if (!_.isEmpty(shopifyProduct) && !_.isEmpty(currentVariant)) {
      fetchProductOrVariant(shopifyProduct.storefrontId, currentVariant.storefrontId).then((fetchedProduct) => {
        setProductVariant(fetchedProduct);
      });
    } else {
      setProductVariant(currentVariant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopifyProduct, currentVariant]);

  const CartCtaCopy = useMemo(
    () => (
      <CartCtaContainer>
        <CartCta
          disabled={!isAvailable}
          className={!isAvailable ? 'disabled' : undefined}
          onClick={() => {
            handleAddToCart(currentVariant);
          }}
          starWarsStyle={currentVariant?.productTitle === 'G1T4-M1N1' || currentVariant?.productTitle === 'Grogu'}
        >
          ADD TO CART
        </CartCta>
      </CartCtaContainer>
    ),
    [currentVariant, isAvailable, handleAddToCart],
  );

  // Expand product sections
  const onExpandShipping = () => {
    if (!isExpandShipping) {
      setIsExpandShipping(true);
    } else {
      setIsExpandShipping(false);
    }
    return isExpandShipping;
  };

  const onExpandWhatsIncluded = () => {
    if (!isExpandWhatsIncluded) {
      setIsExpandWhatsIncluded(true);
    } else {
      setIsExpandWhatsIncluded(false);
    }
    return isExpandWhatsIncluded;
  };

  const onExpandProductDetails = () => {
    if (!isExpandProductDetails) {
      setIsExpandProductDetails(true);
    } else {
      setIsExpandProductDetails(false);
    }
    return isExpandProductDetails;
  };

  const onPropositionClick = () => {
    if (!isExpandProposition) {
      setPropositionTitle(collapseText);
      setIsExpandProposition(true);
    } else {
      setPropositionTitle(expandText);
      setIsExpandProposition(false);
    }

    return propositionTitle;
  };

  return (
    <ShopProductCol {...storyblokEditable(blok)} xs={8} md={10} lg={12} sectionMarginTop={sectionMarginTop}>
      <Row>
        <Col xs={8} md={10} lg={12}>
          {/* Back navigation */}
          <BackRow>
            <Col xsOffset={1} xs={6} md={8} lg={10}>
              <BackLink to={withPrefix(SHOP_URL)} starWarsTheme={blok.starWarsTheme}>
                <ArrowLNavy alt="Back arrow" /> Back
              </BackLink>
            </Col>
          </BackRow>

          {/* Product info */}
          <Row>
            {/*  /!* Top product carousel *!/*/}
            <Col xs={8} sm={8} mdOffset={1} md={6} lg={7}>
              {currentVariant?.topGallery?.length > 0 && (
                <ProductVariantCarousels
                  topCarousels={currentVariant?.topGallery}
                  activeVariant={currentVariant?.title}
                />
              )}
            </Col>

            {/* Product details */}
            <Col xsOffset={1} xs={5} mdOffset={0} md={3} className={blok.starWarsTheme && 'max-[414px]:!ml-[30px]'}>
              <ProductPricesCtaContainer>
                {/* Product logo */}
                {blok.logo?.length > 0 &&
                  blok.logo.map((nestedBlok) => <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />)}

                {/* product title */}
                {blok.productPageIncludeTitle && (
                  <div className="mt-[45px] sm:mt-[36px] md:mt-[27px] lg:mt-[30px]">
                    <h2 className="typography-body-xxl color-beige-100">{productPageTitleModified}</h2>
                  </div>
                )}

                {/* Color variant price */}
                {selectedVariantPrice}

                {/* Loox rating */}
                {shopifyProduct && !blok.hideRating ? (
                  <LooxRating link="#productReviews" shopifyId={shopifyProduct.shopifyId} />
                ) : undefined}

                {/* Product description */}
                {currentVariant && (
                  <ProductVariantDescription currentVariant={currentVariant} starWarsTheme={blok.starWarsTheme} />
                )}

                {/* Color variants */}
                <ColorsCtaContainer>
                  <div>
                    {productVariants.length > 0 && (
                      <ProductVariantSelector
                        productVariants={productVariants}
                        currentVariant={currentVariant}
                        onVariantChanged={handleVariantChanged}
                        starWarsTheme={blok.starWarsTheme}
                      />
                    )}
                    <CurrentColorCopy>
                      {currentVariant?.title}
                      {currentVariant?.limitedEdtion && <LimitedEditionTag />}
                    </CurrentColorCopy>
                  </div>

                  {/* Add to cart button & stock status */}
                  {CartCtaCopy}
                </ColorsCtaContainer>

                <div className="mt-[20px]">
                  {/* Shop Pay Installments */}
                  {!_.isEmpty(currentVariant) && (
                    <div className="flex flex-col items-center">
                      <img
                        className="w-[166px] min-w-[166px] aspect-[2097/653] !h-auto"
                        alt="Shop Pay Installments"
                        src={blok.starWarsTheme ? ShopPayInstallmentsLogoWhite : ShopPayInstallmentsLogoBlack}
                      />
                      <span
                        className={`typography-body-xxs sm:typography-body-s
                        ${blok.starWarsTheme ? '!text-beige-100' : '!text-navy-600'} text-center md:text-left`}
                      >
                        {currentVariant.shopPayInstallmentsText}
                      </span>
                    </div>
                  )}
                </div>

                {/* Showfields demo */}
                {currentVariant?.ctaDemo?.map((nestedBlok) => (
                  <Col xs={8} md={8} lg={7} key={nestedBlok._uid}>
                    <div className="showfields-wrapper">
                      <StoryblokComponent blok={nestedBlok} />
                    </div>
                  </Col>
                ))}
              </ProductPricesCtaContainer>

              {/* Scrollable product info */}
              <ProductScrollableInfo starWarsTheme={blok.starWarsTheme}>
                {/* Shipping */}
                {currentVariant?.isShowShipping && currentVariant?.shippingDescription && (
                  <ScrollableSections starWarsTheme={blok.starWarsTheme}>
                    <CollapseArrow
                      onExpandChange={onExpandShipping}
                      buttonCopy="free shipping"
                      pageType="productTemplate"
                      color="navy600"
                    />
                    <AnimateHeight duration={250} height={isExpandShipping ? 'auto' : 0} animateOpacity>
                      <div className="description">{renderRichTextReact(currentVariant.shippingDescription)}</div>
                    </AnimateHeight>
                  </ScrollableSections>
                )}

                {/* What's included */}
                {currentVariant?.isShowWhatsIncluded && currentVariant?.whatsIncludedDescription && (
                  <ScrollableSections starWarsTheme={blok.starWarsTheme}>
                    <CollapseArrow
                      onExpandChange={onExpandWhatsIncluded}
                      buttonCopy="what's included"
                      pageType="productTemplate"
                      color="navy600"
                    />
                    <AnimateHeight duration={250} height={isExpandWhatsIncluded ? 'auto' : 0} animateOpacity>
                      <div className="description">{renderRichTextReact(currentVariant.whatsIncludedDescription)}</div>
                    </AnimateHeight>
                  </ScrollableSections>
                )}

                {/* What's included */}
                {currentVariant?.isShowProductDetails && currentVariant?.productDetailsDescription && (
                  <ScrollableSections starWarsTheme={blok.starWarsTheme}>
                    <CollapseArrow
                      onExpandChange={onExpandProductDetails}
                      buttonCopy="product details"
                      pageType="productTemplate"
                      color="navy600"
                    />
                    <AnimateHeight duration={250} height={isExpandProductDetails ? 'auto' : 0} animateOpacity>
                      <div className="description">{renderRichTextReact(currentVariant.productDetailsDescription)}</div>

                      {currentVariant?.isShowProposition && currentVariant?.propositionDescription && (
                        <PropositionSection>
                          <h3>
                            <span onClick={onPropositionClick} aria-hidden="true">
                              {propositionTitle}
                            </span>{' '}
                            65 warning for California residents.
                          </h3>
                          <AnimateHeight duration={250} height={isExpandProposition ? 'auto' : 0} animateOpacity>
                            <div>
                              <div className="description">
                                {renderRichTextReact(currentVariant?.propositionDescription)}
                              </div>
                            </div>
                          </AnimateHeight>
                        </PropositionSection>
                      )}
                    </AnimateHeight>
                  </ScrollableSections>
                )}
              </ProductScrollableInfo>
            </Col>
          </Row>
        </Col>
      </Row>

      {currentVariant && !_.isEmpty(currentVariant.fullWidthImageWithTwoColumnCaption1) && (
        <Row>
          <div className="mt-[82px] sm:mt-[112px] lg:mt-[120px] mx-[30px] sm:mx-[12.5%] md:mx-[10%] lg:mx-[8.33333%]">
            <FullWidthImageWithTwoColumnCaption
              blok={currentVariant.fullWidthImageWithTwoColumnCaption1.find((el) => el)}
            />
          </div>
        </Row>
      )}

      {currentVariant && (!_.isEmpty(currentVariant.productCard1) || !_.isEmpty(currentVariant.productCard2)) && (
        <RowTW className="mx-[30px] sm:mx-[5%] md:mt-[80px]">
          <ColTW xs={8} md={5} lg={6} className="mt-[50px] sm:mt-[90px] md:mt-[0] md:pr-[40px] flex-1">
            <ProductCard blok={currentVariant.productCard1.find((el) => el)} />
          </ColTW>
          <ColTW xs={8} md={5} lg={6} className="mt-[50px] sm:mt-[90px] md:mt-[0] md:pl-[40px] flex-1">
            <ProductCard blok={currentVariant.productCard2.find((el) => el)} />
          </ColTW>
        </RowTW>
      )}

      {currentVariant && !_.isEmpty(currentVariant.fullWidthImageWithOneColumnCaption1) && (
        <Row>
          <div className="mt-[82px] sm:mt-[112px] lg:mt-[120px] mx-[30px] sm:mx-[12.5%] md:mx-[10%] lg:mx-[8.33333%]">
            <FullWidthImageWithOneColumnCaption
              blok={currentVariant.fullWidthImageWithOneColumnCaption1.find((el) => el)}
            />
          </div>
        </Row>
      )}

      {/* Bottom carousel */}
      {currentVariant?.bottomGallery?.length > 0 && (
        <BottomContainerRow>
          <Col xs={8} sm={8} md={10} lg={12}>
            {blok.starWarsTheme ? (
              <>
                <h3
                  className="font-utopiaStdCaptionNormalBold text-beige-100 font-normal italic text-[23px] mb-[20px]
                    mt-[60px] ml-[30px] sm:ml-[12.5vw] md:ml-[8.3vw] md:text-[23px] md:leading-[1.5]
                    md:tracking-[-0.35px] md:mb-[30px] md:mt-[80px] lg:text-[35px] lg:leading-[1.5]
                    lg:tracking-[-0.35px] lg:mb-[40px] lg:mt-[100px]"
                >
                  Out in the real world
                </h3>
                <HorizontalScroller images={currentVariant?.bottomGallery} fullWidth />
              </>
            ) : (
              <BottomCarousel
                bottomCarouselData={currentVariant?.bottomGallery}
                activeVariant={currentVariant.title}
                starWarsTheme={blok.starWarsTheme}
              />
            )}
          </Col>
        </BottomContainerRow>
      )}

      {currentVariant && !_.isEmpty(currentVariant.fullWidthImageWithOneColumnCaption2) && (
        <Row>
          <div className="mt-[82px] sm:mt-[112px] lg:mt-[120px] mx-[30px] sm:mx-[12.5%] md:mx-[10%] lg:mx-[8.33333%]">
            <FullWidthImageWithOneColumnCaption
              blok={currentVariant.fullWidthImageWithOneColumnCaption2.find((el) => el)}
            />
          </div>
        </Row>
      )}

      {/* Loox reviews */}
      {shopifyProduct && !blok.hideRating ? (
        <ProductReviewsRow id="productReviews">
          <Col xsOffset={1} xs={6} md={8} lg={10}>
            <LooxReviews shopifyId={shopifyProduct.shopifyId} />
          </Col>
        </ProductReviewsRow>
      ) : undefined}
    </ShopProductCol>
  );
};

ShopProduct.propTypes = { blok: object.isRequired };

export default ShopProduct;
